import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import * as Luxon from 'luxon';
import LuminisDatePickerContainer from './LuminisDatePickerContainer.jsx';
import ConfirmationUtils from '@js/confirmation-utils.es6.js';
import { AppointmentTypes } from '@src/js/constants.js';
import DynamicAppointmentFormUtils from '@src/js/services/dynamic-appointment-form-utils.js';
import SessionStorageUtils from '@js/utils/session-storage-utils.es6.js';
import { ACCESSIBILITY_TYPE, SESSION_STORAGE_KEYS } from '@js/constants/constants-imports.es6.js';
import SchedulingResultsAppointmentConfirmationContentType from '@js/content-types/scheduling-results-appointment-confirmation-content-type.es6.js';
import {LuminisServiceUtils} from '@js/utils/utils-imports.es6.js';

const LuminisDatePicker = props => {
  const [dateSelected, setDateSelected] = useState(props.selectedDate);
  const [currentDateText, setCurrentDateText] = useState(
    props.selectedDate ? props.selectedDate.toLocaleString('default', {
      month: 'long',
      day: 'numeric',
  }): '');
  const [selectedTimeSlots, setSelectedTimeSlots] = useState(props.originalTimeSlots);
  const [slotSelected, setSlotSelected] = useState(props.slotSelected);
  const [overlayActive, setOverlayActive] = useState(false);
  let datepickerRef = useRef(null);

  const [durationMins, setDurationMins] = useState(props.slotSelected);
  const [deptIdSelected, setDeptIdSelected] = useState(props.slotSelected);
  const [accessibility, setAccessibility] = useState(props.slotSelected);
  const [appointmentType, setAppointmentType] = useState(props.slotSelected);
  const [insuranceRequirement, setInsuranceRequirement] = useState(props.slotSelected);
  const [referralRequirement, setReferralRequirement] = useState(props.slotSelected);
  const [appointmentNotesRequired, setAppointmentNotesRequired] = useState(props.slotSelected);
  const [visitType, setVisitType] = useState(props.slotSelected);
  const luminisServiceUtils = new LuminisServiceUtils();
  const errorPage = document.querySelector(".field--name-field-provider-epic-error-page a").href;

  useEffect(() => {
    const timeBox = document.querySelector(".time-box");
    if (timeBox) {
      timeBox.addEventListener('click', () => {
        openDatePicker();
      });
    }
  });

  const dateOnChange = changedDate => {
    setDateSelected(changedDate);

    if (changedDate) {
      const monthString = changedDate.toLocaleString('default', {
        month: 'long',
        day: 'numeric',
      });
      setCurrentDateText(monthString);

      const isoDate = Luxon.DateTime.fromJSDate(changedDate, {zone: "America/New_York"}).toISODate();

      props.timesList.forEach(times => {
        if (times[0] === isoDate) {
          setSelectedTimeSlots(times[1]);
        }
      });
    }
  };

  const slotOnChange = slotChosen => {
    const isTelehealthVisit = DynamicAppointmentFormUtils.isVisitTypeTelehealth();
    const visitTypesParsed = JSON.parse(slotChosen.target.dataset.visittypes);
    let visitSelected = '';
    let appointmentSelected = '';

    visitTypesParsed.forEach(visitType => {
      if (visitType.appointmentLocation === AppointmentTypes.TELEMEDICINE && isTelehealthVisit) {
        visitSelected = visitType.visitTypeId;
        appointmentSelected = visitType.appointmentLocation;
      } else if (visitType.appointmentLocation !== AppointmentTypes.TELEMEDICINE && !isTelehealthVisit) {
        visitSelected = visitType.visitTypeId;
        appointmentSelected = visitType.appointmentLocation;
      }
    });

    const insuranceRequirement = slotChosen.target.dataset.insurancerequirement;
    const referralRequirement = slotChosen.target.dataset.referralrequirement;
    const appointmentNotesRequired = slotChosen.target.dataset.appointmentnotesrequired;

    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.INSURANCE_REQUIREMENT, insuranceRequirement);
    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.REFERRAL_REQUIREMENT, referralRequirement);
    SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.APPOINTMENT_NOTES_REQUIRED, appointmentNotesRequired);

    const schedulingResultsAppointmentConfirmationContentType = new SchedulingResultsAppointmentConfirmationContentType();
    if (schedulingResultsAppointmentConfirmationContentType.isRendered) {
      schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph.updateInsuranceRequirements();
      schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph.updateReferralRequirements();
      schedulingResultsAppointmentConfirmationContentType.appointmentForm.paragraph.updateAppointmentNotesRequired();
    }

    setVisitType(visitSelected);
    setAppointmentType(appointmentSelected);
    setSlotSelected(slotChosen.target.dataset.slotvalue);
    setDurationMins(slotChosen.target.dataset.durationmins);
    setAccessibility(slotChosen.target.dataset.accessibility);
    setInsuranceRequirement(slotChosen.target.dataset.insurancerequirement);
    setReferralRequirement(slotChosen.target.dataset.referralrequirement);
    setAppointmentNotesRequired(slotChosen.target.dataset.appointmentnotesrequired);
    setDeptIdSelected(slotChosen.target.dataset.deptid);
  };

  const closeDatePicker = () => {
    if (datepickerRef) {
      datepickerRef.setOpen(false);
    }
  }

  const openDatePicker = () => {
    if (datepickerRef) {
      datepickerRef.setOpen(true);
    }
  }

  const toggleOverlayActive = () => {
    setOverlayActive(true);

    const datepicker = document.querySelector('.react-datepicker__tab-loop');
    datepicker.scrollIntoView({block: "start", behavior: "smooth"});
  }

  const onCloseToggleAndReset = () => {
    setOverlayActive(false);
    // set slot selected back to session storage
    const slotSelected = sessionStorage.getItem('reservedTimeSlot');

    if (slotSelected) {
      const dateSelected = DynamicAppointmentFormUtils.convertFromISOtoJSDate(slotSelected);
      setSlotSelected(slotSelected);
      setDateSelected(dateSelected);
    } else {
      setDateSelected(null);
    }
  }

  const reserveTimeSlot = async (timeSlot) => {
    let minsToReserve = sessionStorage.getItem('minsToReserve');
    if (!minsToReserve) {
      minsToReserve = document.querySelector(
        '.field--name-field-default-mins-to-reserve-sl .field__item').textContent;
      sessionStorage.setItem('minsToReserve', minsToReserve);
    }
    try {
      const epicProviderId = sessionStorage.getItem('epicProviderId');

      let reservationKey;

      if (accessibility.toUpperCase() === ACCESSIBILITY_TYPE.DIRECT) {
        const response = await luminisServiceUtils.reserveDirectSchedulingTimeSlot(visitType, deptIdSelected, epicProviderId, timeSlot, durationMins, minsToReserve, errorPage);
        reservationKey = response?.result;
      }
      else {
        const response = await luminisServiceUtils.reserveOpenSchedulingTimeSlot(visitType, deptIdSelected, epicProviderId, timeSlot, durationMins, minsToReserve, errorPage);
        reservationKey = response?.result;
      }

      if (!reservationKey) {
        const unavailableSlotIdentifier = `${epicProviderId}|${deptIdSelected}|${timeSlot}`;
        SessionStorageUtils.setItem(SESSION_STORAGE_KEYS.UNAVAILABLE_SLOT, unavailableSlotIdentifier);

        // TODO LH-3008, LH-3009, LH-3010 - the the user "oops, this slot was just taken" and send them back to scheduling results to select a new time
        window.location.href = errorPage;
        return;
      }

      sessionStorage.setItem('epicDepartmentId', deptIdSelected);
      sessionStorage.setItem('reservationKey', reservationKey);
      sessionStorage.setItem('reservedTimeSlot', `${timeSlot}`);
      sessionStorage.setItem('slotAccessibilityType', `${accessibility}`);
      sessionStorage.setItem('durationMins', `${durationMins}`);
      sessionStorage.setItem('appointmentType', `${appointmentType}`);
      sessionStorage.setItem('insuranceRequirement', `${insuranceRequirement}`);
      sessionStorage.setItem('referralRequirement', `${referralRequirement}`);
      sessionStorage.setItem('appointmentNotesRequired', `${appointmentNotesRequired}`);
      sessionStorage.setItem('epicAppointmentType', `${visitType}`);
      ConfirmationUtils.setVisitType(appointmentType);

      // call onclose to make sure call came back in time.
      onCloseToggleAndReset();
    } catch {
      // we are redirecting to an error page and this is handled already.
    }
  }

  const unreserveAndThenReserveTimeSlot = async (timeSlot) => {
    const keyToRelease = sessionStorage.getItem('reservationKey');
    if (keyToRelease) {
      try {
        await luminisServiceUtils.unreserveTimeSlot(keyToRelease);
      } catch {
        // we don't really care; we need to continue error already handled and logged
      }

      sessionStorage.setItem('reservationKey', '');
      sessionStorage.setItem('reservedTimeSlot', '');
      await reserveTimeSlot(timeSlot);
    }
    else {
      await reserveTimeSlot(timeSlot);
    }
  }

  const saveDatePickerAndReserveTimeSlot = async () => {
    // should come first as the await seems to confuse the datepicker events.
    closeDatePicker();

    // Done was hit but no time changes were made
    if (sessionStorage.getItem('reservedTimeSlot') !== slotSelected) {
      await unreserveAndThenReserveTimeSlot(slotSelected);
      DynamicAppointmentFormUtils.updateTime(slotSelected);
    }
  }

  const validateFieldsAndSaveDatePickerAndReserveTimeSlot = async () => {
    if (slotSelected && sessionStorage.getItem('reservedTimeSlot') !== slotSelected) {
      await unreserveAndThenReserveTimeSlot(slotSelected);
    }

    // get selected mnemonic from dropdown
    const npi = document.querySelector('.field--name-field-provider-npi-number').textContent;
    DynamicAppointmentFormUtils.redirectToScheduleOnline(npi, props.currentMnemonic, deptIdSelected);
  }

  const container = ({ className, children }) => {
    return (
      <LuminisDatePickerContainer
        currentDateText={currentDateText}
        children={children}
        className={className}
        dateSelected={dateSelected}
        slotSelected={slotSelected}
        timesList={props.timesList}
        selectedTimeSlots={selectedTimeSlots}
        slotOnChange={slotOnChange}
        getHoursFromDate={DynamicAppointmentFormUtils.getHoursFromDate}
        closeDatePicker={closeDatePicker}
        saveDatePickerAndReserveTimeSlot={saveDatePickerAndReserveTimeSlot}
        closeCalendarText={props.closeCalendarText}
        doneText={props.doneText}
        timesText={props.timesText}
        isTelehealthSelected={props.isTelehealthSelected}
        showInline={props.showInline}
        continueText={props.continueText}
        continueOnSubmit={validateFieldsAndSaveDatePickerAndReserveTimeSlot}
        selectedDayAndTimeText={props.selectedDayAndTimeText}
        selectedDayAndTimeTextJoiner={props.selectedDayAndTimeTextJoiner}
      />
    );
  };

  return (
    <>
      <div className={`mobile-overlay mobile-only ${overlayActive ? 'active' : ''}`} />
      <DatePicker
        selected={dateSelected}
        onChange={date => dateOnChange(date)}
        calendarContainer={container}
        shouldCloseOnSelect={false}
        minDate={props.minDate}
        maxDate={props.maxDate}
        excludeDates={props.excludedDates}
        useWeekdaysShort
        dateFormat="MMMM dd, yyyy"
        ref={(r) => datepickerRef = r}
        onCalendarClose={onCloseToggleAndReset}
        onCalendarOpen={toggleOverlayActive}
        placeholderText={props.chooseADayAndTimeText}
        popperPlacement="bottom-start"
        key={props.key}
        inline={props.showInline}
      />
      {!props.showInline &&
        <button className="choose-day-or-time styleless-button"
                onClick={openDatePicker} type='button'>
          <svg className="calendar-icon" width="24" height="24"
               viewBox="0 0 24 24" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd"
                  d="M19 4h-1V3c0-.55-.45-1-1-1s-1 .45-1 1v1H8V3c0-.55-.45-1-1-1s-1 .45-1 1v1H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 15c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1V9h14v10zM9 13v-2H7v2h2zm2-2h2v2h-2v-2zm6 2v-2h-2v2h2z"
                  fill="#248473"/>
          </svg>
          {props.chooseADayAndTimeText}
        </button>
      }
    </>
  );
};

export default LuminisDatePicker;
